import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Empty, Row, Spin, Tag } from "antd";
import moment from "moment";
import { api } from "@redux/api";
import { buildFacilityPath } from "@/utils/route-context";
import { formatContractRef, formatServiceRequestRef } from "@/utils/entity-ref";
import {
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
} from "@/containers/contract/constants";

const SR_STATUS_COLORS = {
  submitted: "default",
  reviewing: "processing",
  quote_sent: "warning",
  quote_approved: "success",
  quote_rejected: "error",
  in_progress: "processing",
  resolved: "success",
  closed: "default",
};

const SR_STATUS_LABELS = {
  submitted: "Submitted",
  reviewing: "Reviewing",
  quote_sent: "Quote Sent",
  quote_approved: "Quote Approved",
  quote_rejected: "Quote Rejected",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

function formatDateTime(value) {
  return value ? moment(value).format("YYYY-MM-DD HH:mm") : "-";
}

const FieldRow = ({ label, children }) => (
  <div style={{ display: "flex", marginBottom: 8, fontSize: 13 }}>
    <span style={{ width: 110, color: "#888", flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: "break-word" }}>
      {children != null && children !== "" ? children : "-"}
    </span>
  </div>
);

const StatusTag = ({ status }) => (
  <Tag color={SR_STATUS_COLORS[status] || "default"} style={{ marginInlineEnd: 0 }}>
    {SR_STATUS_LABELS[status] || status || "-"}
  </Tag>
);

const ContractStatusTag = ({ status }) => (
  <Tag
    color={CONTRACT_STATUS_COLORS[status] || "default"}
    style={{ marginInlineEnd: 0 }}
  >
    {CONTRACT_STATUS_LABELS[status] || status || "-"}
  </Tag>
);

/**
 * Workorder Related Tab：展示工单关联的 Service Request / Tenant Request /
 * Contract Request / PM。四类关联均非必须——为 null 的卡片不渲染，全空时
 * 显示整体空态；service_request 与 tenant_request 互斥（后端按创建者
 * role==5 归类）。
 * antd TabPane 首次激活才挂载子组件，因此 useEffect 即"Tab 首次激活时懒加载"，
 * 不会随详情页首屏多打一次请求。
 *
 * ⚠️ 本文件与 FacilityKPI-Contract 门户版同名组件为双仓各一份的共享件：
 * 门户版无 PM 卡片，数据走 /api/v2/contract/ 前缀。改动请同步比对。
 */
const WorkorderRelatedTab = ({ id }) => {
  const navigate = useNavigate();
  // 当前路由为 /facility/:companyId/:facilityId/workorder-detail/:id，
  // 后端返回的 detail_route 是 legacy 叶子路径（/facility/xxx/:id），
  // 直接 navigate 会因匹配不到 canonical 路由而"地址栏变了页面不切换"，
  // 必须经 buildFacilityPath 补全公司/设施前缀。
  const { companyId, facilityId } = useParams();
  const toFacilityPath = useCallback(
    (legacyPath) => {
      if (!legacyPath) return null;
      const leaf = String(legacyPath).replace(/^\/facility\//, "");
      return buildFacilityPath(leaf, { companyId, facilityId }) || legacyPath;
    },
    [companyId, facilityId]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [related, setRelated] = useState(null);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(false);
    api
      .getWorkorderRelated(id)
      .then((response) => setRelated(response.data || {}))
      .catch((err) => {
        console.error("Failed to load workorder related records:", err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0" }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0" }}>
        <div style={{ marginBottom: 12, color: "#999" }}>
          Failed to load related records.
        </div>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  const serviceRequest = related?.service_request;
  const tenantRequest = related?.tenant_request;
  const pm = related?.pm;
  const contract = related?.contract;

  if (!serviceRequest && !tenantRequest && !pm && !contract) {
    return <Empty description="No related records" style={{ padding: "48px 0" }} />;
  }

  const renderRequestCard = (record, title) => (
    <Col xs={24}>
      <Card size="small" title={title}
        extra={
          <Button
            type="link"
            size="small"
            onClick={() => navigate(toFacilityPath(record.detail_route))}
          >
            View Details
          </Button>
        }
      >
        <FieldRow label="Title">{record.title}</FieldRow>
        <FieldRow label="ID">{formatServiceRequestRef(record.id)}</FieldRow>
        <FieldRow label="Status">
          <StatusTag status={record.status} />
        </FieldRow>
        <FieldRow label="Priority">{record.priority}</FieldRow>
        <FieldRow label="Category">{record.category_name}</FieldRow>
        <FieldRow label="Created By">{record.createdby_name}</FieldRow>
        <FieldRow label="Created Date">{formatDateTime(record.created_date)}</FieldRow>
      </Card>
    </Col>
  );

  return (
    <Row gutter={[16, 16]}>
      {serviceRequest && renderRequestCard(serviceRequest, "Service Request")}
      {tenantRequest && renderRequestCard(tenantRequest, "Tenant Request")}
      {contract && (
        <Col xs={24}>
          <Card
            size="small"
            title="Contract Request"
            extra={
              contract.detail_route ? (
                <Button
                  type="link"
                  size="small"
                  onClick={() => navigate(toFacilityPath(contract.detail_route))}
                >
                  View Details
                </Button>
              ) : null
            }
          >
            <FieldRow label="Title">{contract.title}</FieldRow>
            <FieldRow label="ID">{formatContractRef(contract.id)}</FieldRow>
            <FieldRow label="Status">
              <ContractStatusTag status={contract.status} />
            </FieldRow>
            <FieldRow label="Priority">{contract.priority}</FieldRow>
            <FieldRow label="Category">{contract.category_name}</FieldRow>
            <FieldRow label="Contractor">{contract.contractor_name}</FieldRow>
            <FieldRow label="Due Date">
              {contract.due_date
                ? moment(contract.due_date).format("YYYY-MM-DD")
                : "-"}
            </FieldRow>
            <FieldRow label="Created Date">
              {formatDateTime(contract.created_date)}
            </FieldRow>
          </Card>
        </Col>
      )}
      {pm && (
        <Col xs={24}>
          <Card
            size="small"
            title="Preventive Maintenance"
            extra={
              <Button
                type="link"
                size="small"
                onClick={() =>
                  navigate(toFacilityPath(`/facility/pm-plan-detail/${pm.pm_plan_id}`))
                }
              >
                View Details
              </Button>
            }
          >
            <FieldRow label="PM Plan">{pm.pm_plan_name}</FieldRow>
            <FieldRow label="Cycle">{pm.cycle_type}</FieldRow>
            <FieldRow label="Wrapper">
              {pm.wrapper_title}
              {pm.wrapper_status ? (
                <Tag style={{ marginLeft: 8 }}>{pm.wrapper_status}</Tag>
              ) : null}
            </FieldRow>
            <FieldRow label="Wrapper ID">{`#${pm.wrapper_id}`}</FieldRow>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default WorkorderRelatedTab;
